import { Vehicle } from "../types/vehicle";
import { ScheduledBatteryCheckList } from "../types/workshopSession";
import { BackofficeError, FetchFromBackoffice } from "../utils/requests";
import { PostToBackoffice } from "../utils/commands";

export const VIN_SERVICE_BUSY_MESSAGE =
  'The VIN lookup service is temporarily overloaded. Wait a moment and try again.';

export const isServiceUnavailableError = (error: unknown) =>
  error instanceof BackofficeError && (error.status === 503 || error.status === 429);

/** Falls back to the caller's message unless the service reported that it is overloaded. */
export const describeVinLookupError = (error: unknown, fallback: string) =>
  isServiceUnavailableError(error) ? VIN_SERVICE_BUSY_MESSAGE : fallback;

export type VehicleUnknownType = {
  workshopId: string;
  vin: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number | string;
  checkedInUtc: string;
  workshopSessionId?: string;
};

export const fetchCheckedInVehicles = (workshopId: string) =>
  FetchFromBackoffice<Vehicle[]>(`portal/workshops/${workshopId}/vehicles/checkedin`)

export const fetchBatteryChecks = (workshopId: string, date: string) =>
  FetchFromBackoffice<ScheduledBatteryCheckList[]>(`portal/workshops/${workshopId}/battery-checks/due/${date}`)

export const fetchVehiclesWithUnknownTypes = (workshopId: string) =>
  FetchFromBackoffice<VehicleUnknownType[]>(
    `portal/workshops/${workshopId}/vehicles/checkedin/unknown-types`
  )

export type VinLookupMatch = {
  make: string;
  makeId?: number | string;
  model: string;
  modelId?: number | string;
  variant?: string | null;
  fromYear: number;
  toYear: number | null;
  estimatedManufacturingYear: number;
  vehicleType?: string;
  regEx?: string;
  externalKey?: string;
  variantId?: string;
};

export type VinLookupResponse = {
  vin: string;
  wmi: string;
  matchStatus: number;
  matchStatusString: string;
  matches: VinLookupMatch[];
};

export type VehicleTypeAIResponse = {
  make: string;
  model: string;
  fromYear: number;
  toYear: number;
  tareKg: number;
  lengthMm: number;
  heightMm: number;
  widthMm: number;
  powerTrain: string;
  key: string;
  variantName: string;
};

export type VinRegexResponse = {
  regexPattern: string;
  wmiDescription: string;
  vdsDetails: string;
  isPos9CheckDigit: boolean;
  visInformation: string;
  isPos10Year: boolean;
  sequentialNumber: string;
  variantName: string;
};

export const lookupVehicleByVin = (rawVin: string) =>
  FetchFromBackoffice<VinLookupResponse>(
    `app/v2/makes/find/by/vin/${encodeURIComponent(rawVin)}`
  )

export const askAiVehicleTypeByVin = (rawVin: string) =>
  FetchFromBackoffice<VehicleTypeAIResponse>(
    `portal/vin-lookup/${encodeURIComponent(rawVin)}/ask-ai`
  )

export type ChangeVinOnVehiclePayload = {
  workshopId: string;
  workSessionId: string;
  vin: string;
  make: string;
  model: string;
  variant?: string;
  variantId?: string;
  year: string;
};

export const changeVinOnVehicle = (payload: ChangeVinOnVehiclePayload) =>
  PostToBackoffice('cmd/Workshop/ChangeVinOnVehicle', payload);

export type SetCurrentLocationPayload = {
  workSessionId: string;
  currentLocation: string;
};

export const setCurrentLocation = (payload: SetCurrentLocationPayload) =>
  PostToBackoffice('cmd/Vehicle/SetCurrentLocation', payload);

export type VinRegexSuggestion = {
  result: {
    regexPattern: string;
    description: string;
  };
  regex: string;
  vin: string;
  make: string;
  model: string;
};

export const suggestVinRegex = (rawVin: string) =>
  FetchFromBackoffice<VinRegexResponse>(
    `portal/vin-lookup/${encodeURIComponent(rawVin)}/regex`
  )

export const suggestVinRegexImprovement = (
  rawVin: string,
  pattern: string,
  brand: string,
  model: string
) => {
  const query = new URLSearchParams({ pattern, brand, model });
  return FetchFromBackoffice<VinRegexSuggestion>(
    `portal/vin-lookup/${encodeURIComponent(rawVin)}/suggest?${query.toString()}`
  );
}

export type VehicleProcessStatusPayload = {
  workSessionId: string;
  processStatus: number;
  processStatusUpdatedAt: string;
};

export const updateVehicleProcessStatus = (workSessionId: string, progress: number, updatedAtLocalTime: string) =>
  PostToBackoffice('cmd/WorkSession/UpdateProgress', { workSessionId, progress, updatedAtLocalTime });
  